import { useCreateFieldGetter, useIsSectionVisible } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';
import { useHeaderVisibility } from '@/composables/useHeaderVisibility';

/**
 * Composable for Help Header component customisation
 * Returns header container background styling and title configuration
 */
export const useHelpHeaderCustomisation = () => {
  const { getField, isVisible: customisationIsVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.HELP_PAGE,
    sectionKey: PUI.HELP_SECTION_KEYS.HEADER_BACKGROUND,
    blockKey: PUI.HELP_BLOCK_KEYS.HEADER,
  });

  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);

  // uiConfig.header.show takes highest priority over brand customisation (mirrors pl-chat-sdk behaviour)
  const isVisible = useHeaderVisibility(customisationIsVisible);

  return {
    isVisible,
    backgroundColor,
  };
};

/**
 * Composable for Help Header Title customisation
 */
export const useHelpHeaderTitleCustomisation = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.HELP_PAGE,
    sectionKey: PUI.HELP_SECTION_KEYS.HEADER_TITLE,
    blockKey: PUI.HELP_BLOCK_KEYS.HEADER,
  });

  const textContent = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    isVisible,
    textContent,
    textColor,
  };
};

/**
 * Generic composable for Nav Icon customisation
 * @param sectionKey - The section key for the nav icon (NAV_ICON_1 or NAV_ICON_2)
 */
const useHelpNavIconCustomisation = (sectionKey: string) => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.HELP_PAGE,
    sectionKey,
    blockKey: PUI.HELP_BLOCK_KEYS.HEADER,
  });

  const actionColor = getField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR);

  return {
    isVisible,
    actionColor,
  };
};

/**
 * Composable for Nav Icon 1 customisation
 */
export const useHelpNavIcon1Customisation = () => {
  return useHelpNavIconCustomisation(PUI.HELP_SECTION_KEYS.NAV_ICON_1);
};

/**
 * Composable for Nav Icon 2 customisation
 */
export const useHelpNavIcon2Customisation = () => {
  return useHelpNavIconCustomisation(PUI.HELP_SECTION_KEYS.NAV_ICON_2);
};

/**
 * Composable for Help Header Search customisation
 */
export const useHelpHeaderSearchCustomisation = () => {
  const isVisible = useIsSectionVisible({
    registryId: PUI.REGISTRY_IDS.HELP_PAGE,
    sectionKey: PUI.HELP_SECTION_KEYS.SEARCH_INPUT,
  });

  const { getField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.HELP_PAGE,
    sectionKey: PUI.HELP_SECTION_KEYS.HEADER_SEARCH,
    blockKey: PUI.HELP_BLOCK_KEYS.HEADER,
  });

  const placeholderText = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);

  return {
    isVisible,
    placeholderText,
  };
};
